<template>
  <div class="mgt-actions f-h">
    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <div slot="shortcuts" class="v-centerer">
        <div class="legend">
          {{ $t('message.ACTIONS_COMPLETION_DATE') }}
        </div>
        <Switcher v-model="selectedRange" :titles="ranges" />
      </div>

      <button
        class="generic-border generic-border--text-btn generic-border--blue-border"
        @click="excelExport"
      >
        <Icon>{{ downloadMedium }}</Icon>
        <span>{{ $t('message.EXPORT_TO_EXCEL') }}</span>
      </button>
      <div class="field-hint mt5em mb10em">
        {{ $t('message.ACTION_EXPORT_ALL') }}
      </div>
      <h2 class="basic-sub-title">
        {{ $t('message.FILTERS') }}
      </h2>
      <ComponentsList :value="mgtPagesOptions" @edit="paramsEdit" />
    </FiltersZone>
    <div class="f-h-below-filters">
      <div class="mgt-actions__col-headers v-centerer">
        <SortOption class="mgt-actions__col-headers__team" :label="$t('message.TEAM')" />
        <SortOption class="mgt-actions__col-headers__ongoing" :label="$t('message.ONGOING_ACTIONS')" />
        <SortOption class="mgt-actions__col-headers__late" :label="$t('message.LATE_ACTIONS')" />
        <SortOption class="mgt-actions__col-headers__completed" :label="$t('message.COMPLETED_ACTIONS')" />
      </div>
      <div
        v-for="team in teamsData"
        :key="team.id"
        class="mgt-actions__item v-centerer"
        :class="{ selected: teamSelected && team.id === teamSelected.id }"
        @click="teamSelected = (teamSelected && team.id === teamSelected.id) ? null : team"
      >
        <div class="mgt-actions__item__team">{{ teamsPerId[`${team.id}`].name }}</div>
        <div class="mgt-actions__item__ongoing h-v-centerer">
          <table class="mgt-actions__item__pdca-table">
            <tr>
              <th>
                <img :src="require(`@/assets/img/pdca-pending.svg`)" alt="">
              </th>
              <th>
                <img :src="require(`@/assets/img/pdca-plan.svg`)" alt="">
              </th>
              <th>
                <img :src="require(`@/assets/img/pdca-do.svg`)" alt="">
              </th>
              <th>
                <img :src="require(`@/assets/img/pdca-check.svg`)" alt="">
              </th>
              <th>
                <img :src="require(`@/assets/img/pdca-act.svg`)" alt="">
              </th>
            </tr>
            <tr>
              <td class="mgt-actions__item__pdca-table__pending">{{ getPdcaNb(team, 0) }}</td>
              <td class="mgt-actions__item__pdca-table__plan">{{ getPdcaNb(team, 1) }}</td>
              <td class="mgt-actions__item__pdca-table__do">{{ getPdcaNb(team, 2) }}</td>
              <td class="mgt-actions__item__pdca-table__check">{{ getPdcaNb(team, 3) }}</td>
              <td class="mgt-actions__item__pdca-table__act">{{ getPdcaNb(team, 4) }}</td>
            </tr>
          </table>
        </div>
        <div class="mgt-actions__item__late h-v-centerer">
          <Icon>{{ clockMedium }}</Icon>
          <div>{{ getPdcaLate(team) }}</div>
        </div>
        <div class="mgt-actions__item__completed">
          <div class="mgt-actions__item__completed__content h-v-centerer">
            <Icon>{{ tickExtraBold }}</Icon>
            <div>{{ team.completed.length }}</div>
          </div>
          <div v-if="team.ncompletedLate > 0" class="mgt-actions__item__completed__late">
            {{ $t('message.NB_LATE_ACTIONS', { nb: team.ncompletedLate }) }}
          </div>
        </div>
      </div>
    </div>
    <Modal :value="teamSelected != null" class="mgt-actions__team-modal" @input="teamSelected = null">
      <div v-if="teamSelected" class="mgt-actions__team-modal__wrapper">
        <h2 class="basic-sub-title">
          {{ teamsPerId[`${teamSelected.id}`].name }}
        </h2>
        <div v-for="(plan, pInd) in teamSelected.plans" :key="plan.id" class="mgt-actions__team-modal__plan">
          <h3>{{ plan.name }}</h3>
          <div class="mgt-actions__team-modal__actions">
            <PlanActionItem
              v-for="(action, aInd) in plan.actions"
              :id="'paid' + pInd + '-' + aInd"
              :key="action.id"
              :action="action"
              :plan="plan"
              disabled
              dashboard
            />
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Management page for actions synthesis
 *
 * As a management page, MgtActions uses selectedTeamIds from mixins/mgt-pages-teams
 * to filter its content.
 */

import { isEqual } from 'lodash';
import toastr from 'toastr';
// COmponents
import ComponentsList from '@/components/components-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
import SortOption from '@/components/sort-option.vue';
import Switcher from '@/components/switcher.vue';
// Mixins
import mixinMgtPagesOptions from '@/mixins/page-options/mgt-pages.vue';
import mixinMgtPagesTeams from '@/mixins/mgt-pages-teams.vue';
// Utils
import { exportActionsToExcel } from '@/utils/action-utils';
import { clockMedium, downloadMedium, tickExtraBold } from '@/assets/icons';

export default {

  name: 'MgtActions',

  components: {
    ComponentsList,
    FiltersZone,
    Icon,
    Modal,
    PlanActionItem,
    SortOption,
    Switcher,
  },

  mixins: [mixinMgtPagesOptions, mixinMgtPagesTeams],

  data() {
    return {
      clockMedium,
      downloadMedium,
      tickExtraBold,
      ranges: [this.$t('message.SEVEN_DAYS'), this.$t('message.FOUR_WEEKS')],
      teamsData: [],
      keepTeamSelected: null,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.TEAMS_ACTIONS'),
    };
  },

  computed: {
    /** Object: With 'start' and 'end' moment instances */
    dateRange() {
      return {
        start: this.selectedRange === 0 ? this.$moment().subtract(7, 'day') : this.$moment().subtract(4, 'week'),
        end: this.$moment(),
      };
    },
    /** Array: Array of the selected teams, defaults to teams managed by current team */
    mgtPagesOptTeams() {
      return this.selectedTeamIds;
    },
    // Query params
    /** Number: Value of the switcher (index of ranges) */
    selectedRange: {
      get() {
        return Number(this.$route.query.mgtActionsRangeIndex || 0);
      },
      set(range) {
        const query = Object.assign({}, this.$route.query);
        query.mgtActionsRangeIndex = range;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Object: Team data currently focused or null */
    teamSelected: {
      get() {
        return (this.teamsData || []).find(team => team.id === Number(this.$route.query.mgtActionsTeam));
      },
      set(team) {
        const query = Object.assign({}, this.$route.query);
        query.mgtActionsTeam = team === null || team === undefined ? undefined : String(team.id);
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    selectedRange() {
      this.refresh();
    },
    isActionCreationDisplayed() {
      if (this.isActionCreationDisplayed) {
        this.keepTeamSelected = this.teamSelected;
        this.teamSelected = null;
      } else {
        this.teamSelected = this.keepTeamSelected;
        this.keepTeamSelected = null;
      }
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    /** Reload the informations content */
    refresh() {
      const cRange = Object.assign({}, this.dateRange);
      cRange.start = cRange.start.format('YYYY-MM-DD HH:mm:ss');
      cRange.end = cRange.end.format('YYYY-MM-DD HH:mm:ss');
      this.$http.post('/server/management/getActions.php', { teams: this.selectedTeamIds, range: cRange })
        .then((response) => {
          if (response.data && Array.isArray(response.data)) {
            const { data } = response;
            data.forEach((team) => {
              team.ncompletedLate = team.completed.filter(a => this.$moment(a.deadline).isBefore(a.actedOn)).length;
            });

            this.teamsData = response.data;
          }
        });
    },
    /** Returns the number of actions in a PDCA state */
    getPdcaNb(team, pdca) {
      const { plans, ongoing } = team;
      const allActions = [
        ...ongoing,
        ...plans.flatMap(p => p.actions).filter(a => !ongoing.some(({ id }) => a.id === id)),
      ];
      return allActions.filter(a => a.pdca === pdca).length;
    },
    /** Returns the number of current late actions in the action plans of the team */
    getPdcaLate(team) {
      return team.ongoing.filter(a => this.$moment().diff(a.deadline) > 0).length;
    },
    /** Returns the number of actions completed after their deadline */
    getCompletedLate(team) {
      return team.completed.filter(a => this.$moment(a.deadline).isBefore(a.actedOn)).length;
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'teams') {
        query.mgtTeams = option.value.join(',');
      }
      this.$emit('input', option);
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /** Fetch all the problems and export them in a readable format */
    excelExport() {
      this.$http.post('/server/actions/get.php', { exportData: true, teams: this.selectedTeamIds })
        .then((response) => {
          if (!response || !(response.data instanceof Array)) {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
            return;
          }
          exportActionsToExcel(response.data);
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.mgt-actions {
  position: relative;
  overflow: hidden auto;

  .filters-zone .switcher {
    height: 2.2rem;
    margin-left: 0.7rem;
  }

  &__col-headers {
    margin-top: 0.8em;
    height: 1.8em;

    &__team {
      flex: 1;
      justify-content: flex-start;
      margin-left: 1rem;
    }

    &__ongoing, &__late, &__completed {
      flex: 0 0 15rem;
    }
  }

  &__item {
    background: white;
    border-radius: $main-border-radius;
    height: $main-tile-height;
    padding: 0.2rem 0;
    margin-bottom: $page-content-padding;
    cursor: pointer;

    &__team {
      flex: 1;
      margin-left: 1rem;
      font-weight: 600;
    }

    &__ongoing, &__late, &__completed {
      flex: 0 0 15rem;
    }

    &__late {
      .icon {
        width: 1.2rem;
        height: 1.2rem;
        margin-right: 0.5rem;
      }
    }

    &__completed {
      text-align: center;

      &__content {
        color: $color-shizen-green;
        font-weight: 600;

        .icon {
          width: 1.2rem;
          height: 1.2rem;
          margin-right: 0.5rem;
        }
      }

      &__late {
        font-size: 0.9rem;
        margin-top: 0.35rem;
        font-style: italic;
      }
    }

    &__pdca-table {
      th, td {
        padding: .2rem .5rem;
        text-align: center;
        font-weight: 600;

        img {
          display: block;
        }
      }

      &__pending {
        color: #97989B;
      }

      &__plan {
        color: $color-pdca-plan;
      }

      &__do {
        color: $color-pdca-do;
      }

      &__check {
        color: $color-pdca-check;
      }

      &__act {
        color: $color-pdca-act;
      }
    }
  }

  &__team-modal {
    &__wrapper {
      padding: 1rem;
      box-sizing: border-box;
      background: $color-body-background;

      h2.basic-sub-title {
        margin-left: .9rem;
      }
    }

    &__plan {
      background: white;
      border-radius: $main-border-radius;
      padding: 1rem;
      margin-bottom: $page-content-padding;

      h3 {
        margin-top: 0px;
      }
    }

    &__actions {
      width: 100%;
      display: flex;
      flex-wrap: wrap;

      .plan-action-item {
        text-align: left;

        &:not(:last-child) {
          margin-right: $page-content-padding;
        }
      }
    }
  }
}

@media (max-width: $tablet-landscape) {
  .mgt-actions {
    .filters-zone .switcher {
      height: 2rem;
      margin-left: 0.7rem;
    }

    &__col-headers {
      &__ongoing, &__late, &__completed {
        flex: 0 0 12rem;
      }
    }

    &__item {
      &__ongoing, &__late, &__completed {
        flex: 0 0 12rem;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .mgt-actions {
    &__col-headers {
      &__late, &__completed {
        flex: 0 0 9rem;
      }
    }

    &__item {
      &__late, &__completed {
        flex: 0 0 9rem;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .mgt-actions {
    &__col-headers {
      &__ongoing {
        flex: 0 0 10rem;
      }

      &__late {
        flex: 0 0 5rem;
      }
    }

    &__item {
      &__ongoing {
        flex: 0 0 10rem;
      }

      &__late {
        flex: 0 0 5rem;
      }

      &__pdca-table {
        th, td {
          padding: .2rem .1rem;
        }
      }
    }
  }
}
</style>
